import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Card, Badge, Layout, Icon, Banner, Button, Text, BlockStack, InlineStack, InlineGrid, Divider, Box, Popover, ActionList, IndexFilters, useSetIndexFiltersMode, Pagination, Spinner, Checkbox, IndexTable, useIndexResourceState, EmptySearchResult } from '@shopify/polaris';
import { DuplicateIcon, ExternalSmallIcon, EditIcon, DeleteIcon, StatusActiveIcon } from '@shopify/polaris-icons';
import { saveShippingList, setShippingCounts } from '../../store/actions/shippingActions';
import { setToast } from '../../store/actions/configActions';
import WarningCard from '../WarningCard';
import PolarisModal from '../PolarisModal';
import Loader from '../Loader';
import './style.css'
import { useNavigate } from 'react-router-dom';
import constants from '../../store/constants';
import ShippingCardActions from '../ShippingCardActions';
import CustomToggle from '../CustomToggle';
import { toQueryString } from '../../helper';
import useAuthenticatedFetch from '../../hooks/useAuthenticatedFetch';

const AREA_LABELS = {
    no: 'All locations',
    countries: 'Selected countries',
    state: 'Selected states / provinces',
    city: 'Selected cities',
    zip: 'Specific postal codes'
}
const AREA_VALUES = { countries: 'countries', state: 'states', city: 'cities', zip: 'zip_codes' }
const PRICE_LABELS = { no: 'Flat rate', price: 'Cart value', weight: 'Weight', qty: 'Item quantity' }
const UNIT_NAMES = { kg: 'kilogram', lb: 'pound', oz: 'ounce' }

const summarizeValues = list => {
    if (!Array.isArray(list) || list.length === 0) return '—'
    const labels = list.map(v => (v && typeof v === 'object' ? v.label ?? v.value : v)).filter(Boolean)
    if (labels.length <= 2) return labels.join(', ')
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`
}

const areaSummary = shipping => {
    const by = shipping.shipping_by || 'no'
    return {
        label: AREA_LABELS[by] ?? AREA_LABELS.no,
        values: by === 'no' ? 'Every address' : summarizeValues(shipping[AREA_VALUES[by]])
    }
}

const priceSummary = (shipping, currency) => {
    const by = shipping.charge_by || 'no'
    const label = PRICE_LABELS[by] ?? PRICE_LABELS.no
    if (by === 'no') {
        const cost = shipping.default_cost
        return { label, detail: cost !== null && cost !== undefined && cost !== '' ? `${cost} ${currency}` : '—' }
    }
    const count = Array.isArray(shipping.shipping_ranges) ? shipping.shipping_ranges.length : 0
    const ranges = `${count} ${count === 1 ? 'range' : 'ranges'}`
    let type = 'Fixed amount'
    if (by === 'price' && shipping.priceBy === '%') type = 'Percentage'
    else if (by === 'weight' && shipping.cost_by && shipping.cost_by !== 'flat') type = `Per ${UNIT_NAMES[shipping.unit] ?? 'unit'}`
    else if (by === 'qty' && shipping.cost_by && shipping.cost_by !== 'flat') type = 'Per item'
    return { label, detail: `${type} · ${ranges}` }
}
const itemStrings = [
    'All',
    'Active',
    'Draft',
];
const perPage = 30

const ShippingsList = () => {
    const navigate = useNavigate()
    const fetchJson = useAuthenticatedFetch()

    const { shopName, store, features, feedback, payment_id } = useSelector(state => state.ConfigReducer)
    const { shippingList, shippingCounts } = useSelector(state => state.ShippingReducer)
    const [rates] = useState(features.indexOf(constants.RATES) >= 0)

    const dispatch = useDispatch()

    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(null)
    const [totalPages, setTotalPages] = useState(Math.ceil(shippingCounts / perPage))
    const [OpenWarning, setOpenWarning] = useState(false)
    const [Dlt, setDlt] = useState({})
    const [Duplicate, setDuplicate] = useState({})
    const [Id, setId] = useState("")
    const [queryValue, setQueryValue] = useState('');
    const [selected, setSelected] = useState(0);
    const { mode, setMode } = useSetIndexFiltersMode('DEFAULT');
    const [loading, setLoading] = useState(false)
    const [updating, setUpdating] = useState({})
    const [deleting, setDeleting] = useState(false)
    const [bulkSelected, setBulkSelected] = useState([])
    const { selectedResources, allResourcesSelected, handleSelectionChange } = useIndexResourceState(shippingList);
    const { currency: shopCurrency } = useSelector(state => state.ConfigReducer)
    const [counts, setCounts] = useState({ active: null, draft: null })

    const refreshCounts = useCallback(async () => {
        try {
            const [active, draft] = await Promise.all([
                fetchJson(`/shipping?limit=1&offset=0&active=true`, { method: 'GET' }),
                fetchJson(`/shipping?limit=1&offset=0&active=false`, { method: 'GET' })
            ])
            setCounts({ active: active?.total ?? null, draft: draft?.total ?? null })
        } catch (error) {
            console.log(error);
        }
    }, [fetchJson])

    useEffect(() => {
        refreshCounts()
    }, [shippingCounts])

    useEffect(() => {
        setTotalPages(Math.ceil((total !== null && shippingCounts !== total ? total : shippingCounts) / perPage))
    }, [total])

    useEffect(() => {
        if (page >= 1 && page <= totalPages) {
            filterShippingRates(itemStrings[selected], queryValue, page)
        }
    }, [page])

    const tabs = itemStrings?.map((item, index) => ({
        content: item,
        index,
        onAction: () => {
            setSelected(index)
            filterShippingRates(item, queryValue, 1)
        },
        id: `${item}-${index}`,
        isLocked: index === 0
    }));

    const handleFiltersQueryChange = useCallback((value) => {
        filterShippingRates("All", value, 1)
        setQueryValue(value)
        setPage(1)
        setSelected(0)
    }, [],);

    const onHandleCancel = () => handleFiltersQueryChange("");

    const filterShippingRates = async (filter, query, page) => {
        const params = { limit: perPage, offset: perPage * (page - 1), query }
        switch (filter) {
            case "Active":
                params.active = true
                break;
            case "Draft":
                params.active = false
                break;
            default:
                params.active = undefined
                break;
        }
        const url = `/shipping${toQueryString(params)}`
        try {
            setLoading(true)
            const shippings = await fetchJson(url, {
                method: 'GET'
            })
            dispatch(saveShippingList(shippings?.shippingData))
            setTotal(shippings?.total)
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
    }

    const duplicateShipping = async payload => {
        const toast = { "active": true, "error": true, "content": "" }
        if (window.navigator.onLine) {
            const data = { ...payload, "display_name": payload.display_name + " Duplicated" }
            delete data["id"]
            for (const range of data.shipping_ranges) {
                range.id = ""
                range.shipping_id = ""
            }
            if (rates) {
                setDuplicate({ ...Duplicate, [`dup${payload.id}`]: true })
                try {
                    const response = await fetchJson(`/shipping`, {
                        method: 'POST',
                        headers: {
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify({ data })
                    })
                    setDuplicate({ ...Duplicate, [`dup${payload.id}`]: false })
                    if (response?.status >= 500) {
                        toast.content = "Server Error"
                    } else {
                        dispatch(saveShippingList(response?.shippingData))
                        // todo: open setting page
                        toast.content = response?.message
                        toast.error = false
                        navigate(`/rates/${response?.createdId}`)
                    }
                } catch (error) {
                    setDuplicate({ ...Duplicate, [`dup${payload.id}`]: false })
                    toast.content = error.message
                }
            } else {
                toast.content = "This feature is not available in current plan."
            }
        } else {
            toast.content = "Strong internet connection is required to process your request."
        }
        dispatch(setToast({ ...toast }))
    }

    const emptyStateMarkup = (
        loading ? <InlineStack align='center'><Spinner size='large' /></InlineStack> : <EmptySearchResult
            title={'No shipping rates yet'}
            description={'Try changing the filters or search term'}
            withIllustration
        />
    );
    const updateState = (ids, active, stateFun) => {
        stateFun(initial => {
            let _updating = { ...initial }
            for (const id of ids) {
                _updating[id] = active
            }
            return { ..._updating }
        })
    }

    const deleteShipping = id => {
        const ifConnected = window.navigator.onLine;
        if (ifConnected) {
            setDlt({ ...Dlt, [`del${id}`]: true })
            fetchJson(`/shipping?id=${id}&shop=${shopName}&payment_id=${payment_id}`, {
                method: 'DELETE'
            }).then(response => {
                setDlt({ ...Dlt, [`del${id}`]: false })
                if (response?.status >= 500) {
                    console.log("Something went wrong! Please try later.")
                } else if (Object.hasOwnProperty.call(response || {}, "shippingData")) {
                    dispatch(saveShippingList(response.shippingData))
                }
            }).catch(error => {
                setDlt({ ...Dlt, [`del${id}`]: false })
                console.log(error)
            })
        }
    }

    const bulkDelete = async (ids) => {
        try {
            setDeleting(true)
            await fetchJson(`/shipping`, {
                method: 'DELETE',
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ ids: [...ids] })
            })
            const wrappers = ids.map((id) => document.querySelector(`[data-id="${id}"]`));
            wrappers.forEach((wrapper) => {
                if (wrapper) wrapper.classList.add("removing");
            });
            // Wait for animation, then actually remove
            handleSelectionChange("page", false, undefined, undefined)
            setBulkSelected([])
            setTimeout(() => {
                const updatedList = shippingList.filter(item => !ids.includes(item.id));
                dispatch(saveShippingList(updatedList))
                refreshCounts()
            }, 300);
        } catch (error) {
            console.log(error.response);
        } finally {
            setDeleting(false)
            // updateState(ids, false, setDeleting)
        }
    }
    const bulkUpdateStatus = async (ids, status) => {
        try {
            updateState(ids, true, setUpdating)
            await fetchJson(`/shipping/update-status`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ status, ids: [...ids] })
            })

            shippingList.forEach(item => {
                if (ids.includes(item.id)) {
                    item.active = status;
                }
            });
            handleSelectionChange("page", false, undefined, undefined)
            setBulkSelected([])
            refreshCounts()
        } catch (error) {
            console.log(error.response);
        } finally {
            updateState(ids, false, setUpdating)
        }
    }

    const countsReady = counts.active !== null && counts.draft !== null
    const totalRates = countsReady ? counts.active + counts.draft : shippingCounts

    return (<BlockStack gap={400}>
        <InlineGrid columns={{ xs: 1, sm: 3 }} gap={300}>
            <Card>
                <BlockStack gap={100}>
                    <Text as="span" tone="subdued">Total rates</Text>
                    <Text as="strong" variant="headingXl">{totalRates}</Text>
                </BlockStack>
            </Card>
            <Card>
                <BlockStack gap={100}>
                    <Text as="span" tone="subdued">Active</Text>
                    <Text as="strong" variant="headingXl" tone="success">{countsReady ? counts.active : '—'}</Text>
                </BlockStack>
            </Card>
            <Card>
                <BlockStack gap={100}>
                    <Text as="span" tone="subdued">Draft</Text>
                    <Text as="strong" variant="headingXl" tone="subdued">{countsReady ? counts.draft : '—'}</Text>
                </BlockStack>
            </Card>
        </InlineGrid>
        <Card padding={0}>
            <IndexFilters
                queryValue={queryValue}
                queryPlaceholder="Searching in all"
                onQueryChange={handleFiltersQueryChange}
                onQueryClear={onHandleCancel}
                primaryAction={undefined}
                cancelAction={{
                    onAction: onHandleCancel,
                    disabled: false,
                    loading: false,
                }}
                tabs={tabs}
                selected={selected}
                canCreateNewView={false}
                filters={[]}
                mode={mode}
                setMode={setMode}
                loading={loading}
            />
            <IndexTable
                condensed={false}
                resourceName={{ singular: 'shipping rate', plural: 'shipping rates' }}
                itemCount={shippingList?.length}
                selectedItemsCount={allResourcesSelected ? 'All' : selectedResources?.length}
                onSelectionChange={(type, isSelecting, selection, pos) => {
                    setBulkSelected(bulk => {
                        if (type === "single") {
                            bulk = isSelecting ? [...new Set([...bulk, selection])] : bulk.filter(v => v !== selection);
                        } else if (type === "page") {
                            let ids = shippingList.map(item => item.id)
                            bulk = isSelecting
                                ? [...new Set([...bulk, ...ids])] // add (unique)
                                : bulk.filter(v => !ids.includes(v)); // remove
                        }
                        return bulk
                    })
                    handleSelectionChange(type, isSelecting, selection, pos)
                }}
                emptyState={emptyStateMarkup}
                headings={[
                    { title: 'Rate' },
                    { title: 'Delivery area' },
                    { title: 'Price based on' },
                    { title: 'Status' },
                    { title: 'Actions', alignment: "end" }
                ]}
                pagination={totalPages > 0 ? {
                    hasNext: !loading && page < totalPages,
                    hasPrevious: !loading && page > 1,
                    onNext: () => setPage(page => page + 1),
                    onPrevious: () => setPage(page => page - 1),
                    label: `Page ${page} of ${totalPages}`
                } : null}
                bulkActions={[
                    {
                        content: 'Set active',
                        icon: StatusActiveIcon,
                        onAction: () => bulkUpdateStatus(bulkSelected, true),
                    },
                    {
                        content: 'Set draft',
                        icon: StatusActiveIcon,
                        onAction: () => bulkUpdateStatus(bulkSelected, false),
                    },
                    {
                        icon: DeleteIcon,
                        destructive: true,
                        content: 'Delete',
                        onAction: () => setOpenWarning(true),
                    },
                ]}
            >
                {shippingList.length ? shippingList.map((shipping, i) => {
                    const { id, display_name, title, active, currency } = shipping
                    const area = areaSummary(shipping)
                    const price = priceSummary(shipping, currency || shopCurrency)
                    const actionList = [
                        { content: 'Duplicate', loading: Duplicate[`dup${id}`], disabled: Duplicate[`dup${id}`] || !rates, icon: DuplicateIcon, onAction: () => duplicateShipping(shipping) },
                        {
                            content: 'Delete', destructive: true, loading: Dlt[`del${id}`], disabled: Dlt[`del${id}`], icon: DeleteIcon, onAction: () => {
                                setOpenWarning(true)
                                // setId(id)
                                setBulkSelected(bulk => ([...bulk, id]))
                            }
                        }
                    ];
                    return <React.Fragment key={id} data-id={id} className="wrapper">
                        <IndexTable.Row
                            id={id}
                            key={id}
                            selected={selectedResources.includes(id)}
                            position={i}
                        >
                            <IndexTable.Cell>
                                <BlockStack gap={100}>
                                    {title ? <Text variant='bodySm' as="p" tone="subdued">{title}</Text> : null}
                                    <Text variant='headingSm' as="h2">{display_name}</Text>
                                </BlockStack>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                                <BlockStack gap="050">
                                    <Text as="span" fontWeight="semibold">{area.label}</Text>
                                    <Text as="span" variant="bodySm" tone="subdued">{area.values}</Text>
                                </BlockStack>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                                <BlockStack gap="050">
                                    <Text as="span" fontWeight="semibold">{price.label}</Text>
                                    <Text as="span" variant="bodySm" tone="subdued">{price.detail}</Text>
                                </BlockStack>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                                <CustomToggle active={active} loading={updating[id]} handleToggle={value => {
                                    bulkUpdateStatus([id], value)
                                }} />
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                                <InlineStack gap={200} align='end'>
                                    <Button variant="plain" icon={EditIcon} url={`/rates/${id}`} accessibilityLabel={`Edit ${display_name}`} />
                                    <ShippingCardActions actionList={actionList} />
                                </InlineStack>
                            </IndexTable.Cell>
                        </IndexTable.Row>
                    </React.Fragment>
                }) : null}
            </IndexTable>
        </Card>
        <PolarisModal
            active={OpenWarning}
            modalTitle={`Warning`}
            primaryAction={{ content: 'No', onAction: () => { setOpenWarning(false) } }}
            secondaryActions={[{
                content: 'Yes', loading: deleting, disabled: deleting, destructive: true, onAction: async () => {
                    // deleteShipping(Id)
                    await bulkDelete(bulkSelected)
                    setOpenWarning(false)
                }
            }]}
            handleClose={() => setOpenWarning(false)}
            component={<WarningCard width="100%" margin="0px auto" title="Are you confirm to delete this shipping?" status="warning" message="" />}
        />
    </BlockStack>
        // {
        //     feedback ? <div className="feedback">
        //         <Banner
        //             hideIcon={true}
        //             title={`Share your experience & get reward.`}
        //             status="info"
        //         >
        //             <p>Rate & Review on the app store & earn upto one month of Free App Credit!</p>
        //             <div className="feedback-btn"><Button external url={"https://apps.shopify.com/advancedshippingrates#modal-show=ReviewListingModal"}>Add feedback <div style={{ display: 'inline-block', height: "16px", width: "16px", marginLeft: "3px" }}><Icon source={ExternalSmallIcon} /></div></Button></div>
        //             {/* btn color : #54dbef */}
        //         </Banner>
        //     </div> : null
        // }
    )
}

export default ShippingsList;
